/**
 * Delete / Deactivate Account Page - Trade Loop v2.0
 *
 * Offers two options:
 * 1. Soft delete (deactivation) - data preserved, can be reactivated
 * 2. Hard delete (permanent) - profile, personal data and billing history removed for good;
 *    the auth identity is deleted so the account cannot be logged back into.
 *
 * Hard delete is a tombstone, not a cascade (Bug 23). Data jointly owned with a partner —
 * shared referrals and the revenue on them, the conversation, its attachments — belongs to
 * that partner as much as to the departing user, so it survives, attributed to a neutral
 * "Former Trade Loop User" that cannot be messaged or opened. The copy on this screen has to
 * stay honest about that distinction: Apple exercises this flow under Guideline 5.1.1(v).
 *
 * ─── Provider-aware re-authentication ────────────────────────────────────────
 *
 * The reauth control adapts to how the user actually signs in. Password is
 * only shown if the account has an `email` identity; a Google/Apple button is
 * shown if the account has that provider. Prior to this the page hard-required
 * a password, which locked every OAuth-only user out of the deletion flow —
 * an App Store 5.1.1(v) gap that this page was on the wrong side of.
 *
 * The list of linked identities comes from GET /api/auth/identities and drives
 * the choice; the backend re-validates that the credential the user presents
 * matches an identity that actually exists, so the UI cannot be tricked into
 * offering an option the account is not entitled to.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../contexts/ToastContext';
import { api } from '../lib/api';
import { useConnectedAccounts } from '@/api/extensions/useConnectedAccounts';
import { isIOS, isNative } from '@/utils/platform';
import { getGoogleIdToken, mapNativeGoogleError } from '@/services/nativeGoogleAuth';
import { getAppleIdToken, mapNativeAppleError } from '@/services/nativeAppleAuth';
import { renderWebGoogleReauthButton } from '@/services/webGoogleAuth';
import {
  ensureAppleIdentityLoaded,
  getAppleIdTokenWeb,
  isAppleWebUserCancellation,
} from '@/services/webAppleAuth';
import {
  AlertTriangle,
  ArrowLeft,
  Trash2,
  Users,
  MessageSquare,
  UserX,
  Database,
  Shield,
  Power,
  Undo2,
  CreditCard,
  Loader2,
} from 'lucide-react';

type DeletionType = 'soft' | 'hard';
type ReauthProvider = 'email' | 'google' | 'apple';

/**
 * Body shape for DELETE /api/auth/account and /account/permanent. Mirrors
 * ReauthRequestBody on the backend so a typo here is a build failure, not
 * a runtime one.
 */
interface DeleteRequestBody {
  password?: string;
  provider?: 'google' | 'apple';
  id_token?: string;
  nonce?: string;
}

/**
 * Apple reauth: web uses Apple's JS SDK popup (services/webAppleAuth.ts),
 * native iOS uses the OS ASAuthorization sheet via getAppleIdToken. Native
 * Android has NO working Apple path — the plugin's Android Apple flow is a
 * web redirect and a bundled Capacitor build cannot use one (its webview
 * origin is `https://localhost`, which is not a registrable Apple Return
 * URL). This mirrors APPLE_SIGN_IN_AVAILABLE on LoginPage.
 */
const APPLE_REAUTH_AVAILABLE = !isNative || isIOS;

/**
 * Google reauth works on every platform this app runs on. Native uses the OS
 * account picker via getGoogleIdToken; web uses Google Identity Services
 * (services/webGoogleAuth.ts) — a popup / rendered button that returns an
 * id_token in-page without navigating away. See the module comment there for
 * why we do NOT reuse the redirect-based sign-in flow for reauth.
 */
const GOOGLE_REAUTH_AVAILABLE = true;

const DeleteAccountPage = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();

  const [step, setStep] = useState<'choice' | 'warning' | 'confirmation'>('choice');
  const [deletionType, setDeletionType] = useState<DeletionType | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);
  /**
   * Which provider the user picked when the account has more than one option.
   * `null` means we defer to `defaultProvider` below — the picker is only shown
   * when it would give the user a real choice.
   */
  const [chosenProvider, setChosenProvider] = useState<ReauthProvider | null>(null);
  /** In-flight OAuth sheet — used to disable everything while the OS UI is up. */
  const [oauthInFlight, setOauthInFlight] = useState<'google' | 'apple' | null>(null);
  /**
   * Container the Google Identity Services button is rendered INTO on web.
   * Native builds do not use this — they call `getGoogleIdToken` directly from
   * the OS-account-picker path.
   */
  const googleWebButtonContainerRef = useRef<HTMLDivElement | null>(null);
  /** Non-null when GIS could not load / render — surfaced as a fallback message. */
  const [googleWebSetupError, setGoogleWebSetupError] = useState<string | null>(null);

  const {
    data: connectedAccounts,
    isLoading: identitiesLoading,
    error: identitiesError,
  } = useConnectedAccounts();

  /**
   * Which providers this account actually has, filtered down to the ones we can
   * present a reauth control for. `email` always renders as a password field;
   * `google` and `apple` render as buttons when their platform-availability
   * gates are satisfied.
   */
  const availableProviders = useMemo<ReauthProvider[]>(() => {
    if (!connectedAccounts) return [];
    const providers = new Set<ReauthProvider>();
    for (const identity of connectedAccounts.identities) {
      if (identity.provider === 'email') providers.add('email');
      if (identity.provider === 'google' && GOOGLE_REAUTH_AVAILABLE) providers.add('google');
      if (identity.provider === 'apple' && APPLE_REAUTH_AVAILABLE) providers.add('apple');
    }
    return Array.from(providers);
  }, [connectedAccounts]);

  /**
   * Which providers are LINKED to the account regardless of whether we can
   * present a control for them here. Used to explain to the user why an
   * apparent option is greyed out (an Apple-only account on native Android,
   * a Google-only account on web).
   */
  const linkedProviders = useMemo<string[]>(
    () => (connectedAccounts?.identities ?? []).map((identity) => identity.provider),
    [connectedAccounts]
  );

  const defaultProvider: ReauthProvider | null = availableProviders[0] ?? null;
  const activeProvider: ReauthProvider | null = chosenProvider ?? defaultProvider;

  const allWarningsAcknowledged = acknowledgedWarnings;
  const confirmKeyword = deletionType === 'hard' ? 'DELETE' : 'DEACTIVATE';

  const handleChooseDeletionType = (type: DeletionType) => {
    setDeletionType(type);
    setStep('warning');
  };

  const handleProceedToConfirmation = () => {
    if (!allWarningsAcknowledged) {
      showError('Acknowledgement Required', 'Please acknowledge all warnings before proceeding');
      return;
    }
    setStep('confirmation');
  };

  /**
   * Reset the confirmation-step fields when the user backs out or switches
   * provider. Password bleed-through into the next attempt was a bug on the
   * first pass.
   */
  const resetConfirmationFields = () => {
    setConfirmText('');
    setPassword('');
    setAcknowledgedWarnings(false);
  };

  /**
   * Fetch a fresh Google id_token from the OS sheet. Cancelled by the user is
   * NOT surfaced as an error — the sheet closing on purpose is silent.
   */
  const collectGoogleReauth = async (): Promise<{ id_token: string; nonce?: string } | null> => {
    setOauthInFlight('google');
    try {
      const { idToken, nonce } = await getGoogleIdToken();
      return { id_token: idToken, nonce };
    } catch (error) {
      const outcome = mapNativeGoogleError(error);
      if (outcome.kind === 'cancelled') return null;
      const title = 'title' in outcome ? outcome.title : 'Sign-in failed';
      showError(title, outcome.message);
      return null;
    } finally {
      setOauthInFlight(null);
    }
  };

  /**
   * Fetch a fresh Apple id_token. On native iOS this uses the OS sheet via
   * the Capacitor plugin (`getAppleIdToken`); on web it uses Apple's JS SDK
   * popup (`getAppleIdTokenWeb`). Native Android is gated out upstream by
   * `APPLE_REAUTH_AVAILABLE` so we never reach here on that platform.
   *
   * Cancel-is-silent on both paths: dismissing the sheet or closing the
   * popup should never surface as an error toast, only stop the flow.
   */
  const collectAppleReauth = async (): Promise<{ id_token: string; nonce?: string } | null> => {
    setOauthInFlight('apple');
    try {
      if (isNative) {
        const { idToken, nonce } = await getAppleIdToken();
        return { id_token: idToken, nonce };
      }
      const { idToken, nonce } = await getAppleIdTokenWeb();
      return { id_token: idToken, nonce };
    } catch (error) {
      // Native path errors go through the plugin's typed mapper; web path
      // errors come back as Apple JS SDK plain objects. Detect cancellation
      // on both before falling through to the generic native mapper.
      if (isAppleWebUserCancellation(error)) return null;
      const outcome = mapNativeAppleError(error);
      if (outcome.kind === 'cancelled') return null;
      const title = 'title' in outcome ? outcome.title : 'Sign-in failed';
      // On web, mapNativeAppleError falls into its generic branch and its
      // message is not always specific. If we have a raw Apple JS error
      // message, prefer it — a bad `redirectURI` config surfaces there and
      // is invisible otherwise.
      const rawWebMessage =
        !isNative &&
        typeof (error as { message?: string } | null)?.message === 'string'
          ? (error as { message?: string }).message
          : undefined;
      showError(title, rawWebMessage || outcome.message);
      return null;
    } finally {
      setOauthInFlight(null);
    }
  };

  /**
   * Build the request body for the delete endpoint from whichever reauth
   * control the user just satisfied. Returns null when we could not collect
   * one — the caller aborts silently and lets the earlier error toast stand.
   */
  const buildDeletionBody = async (): Promise<DeleteRequestBody | null> => {
    if (activeProvider === 'email') {
      if (!password.trim()) {
        showError('Password Required', 'Please enter your password to confirm');
        return null;
      }
      return { password };
    }
    if (activeProvider === 'google') {
      const oauth = await collectGoogleReauth();
      if (!oauth) return null;
      return { provider: 'google', ...oauth };
    }
    if (activeProvider === 'apple') {
      const oauth = await collectAppleReauth();
      if (!oauth) return null;
      return { provider: 'apple', ...oauth };
    }
    // No reauth control was available — the button should have been disabled,
    // but guard here so a race cannot slip a request through.
    showError('Confirmation Unavailable', 'No sign-in method is available for this account here.');
    return null;
  };

  /**
   * POST the deletion with an already-collected reauth body. Extracted so
   * both the primary "Delete My Account" button and the web Google GIS
   * callback can drive the same code path (auth cookie handling, subscription
   * modal, error copy) without duplicating any of it.
   */
  const submitDeletion = async (body: DeleteRequestBody): Promise<void> => {
    setIsDeleting(true);

    try {
      const endpoint = deletionType === 'hard'
        ? '/api/auth/account/permanent'
        : '/api/auth/account';

      console.log('[DeleteAccount] Calling API endpoint:', endpoint);
      await api.delete(endpoint, body);
      console.log('[DeleteAccount] API call succeeded');

      // Backend handles logout for both soft and hard delete:
      // - Soft delete: admin.signOut() + clear cookies
      // - Hard delete: delete auth user + clear cookies
      // So we don't need to call logout() here

      const successMessage = deletionType === 'hard'
        ? 'Your account has been permanently deleted. Your partners keep their own copy of anything you shared with them.'
        : 'Your account has been deactivated. Simply log in again to reactivate it instantly.';

      showSuccess(
        deletionType === 'hard' ? 'Account Deleted' : 'Account Deactivated',
        successMessage
      );

      console.log('[DeleteAccount] Toast shown, setting timeout for navigation');

      // Force full page navigation to /login after brief delay
      // Using window.location to bypass any route guards
      setTimeout(() => {
        console.log('[DeleteAccount] Navigating to /login');
        window.location.href = '/login';
      }, 2000);
    } catch (error: any) {
      console.error('Delete account error:', error);

      const code = error.response?.data?.code ?? error?.data?.code;

      if (code === 'ACTIVE_SUBSCRIPTION') {
        const subscription = error.response?.data?.subscription ?? error?.data?.subscription;
        setSubscriptionDetails(subscription);
        setShowSubscriptionModal(true);
        setIsDeleting(false);
        return;
      }

      // Provider-aware errors from the backend reauth service. Map to clear
      // copy so the user knows what to try next instead of seeing "Deletion
      // Failed" for something that is really a re-auth prompt.
      if (
        code === 'REAUTH_REQUIRED' ||
        code === 'PROVIDER_MISMATCH' ||
        code === 'INVALID_PASSWORD' ||
        code === 'INVALID_ID_TOKEN'
      ) {
        const title =
          code === 'INVALID_PASSWORD'
            ? 'Incorrect Password'
            : code === 'INVALID_ID_TOKEN'
              ? 'Sign-In Not Verified'
              : 'Please Confirm Your Identity';
        const detail =
          error.response?.data?.error ??
          error?.data?.error ??
          'We could not verify your identity. Please try again.';
        showError(title, detail);
        setIsDeleting(false);
        return;
      }

      showError(
        deletionType === 'hard' ? 'Deletion Failed' : 'Deactivation Failed',
        error.response?.data?.error ??
          error.response?.data?.message ??
          error?.data?.error ??
          `Failed to ${deletionType === 'hard' ? 'delete' : 'deactivate'} account. Please try again.`
      );
      setIsDeleting(false);
    }
  };

  const handleDeleteAccount = async () => {
    console.log('[DeleteAccount] Starting deletion process');

    if (confirmText !== confirmKeyword) {
      showError('Confirmation Required', `Please type ${confirmKeyword} exactly as shown`);
      return;
    }

    const body = await buildDeletionBody();
    if (!body) return;

    await submitDeletion(body);
  };

  const handleCancelSubscriptionAndDelete = async () => {
    setIsDeleting(true);
    setShowSubscriptionModal(false);

    try {
      // First, cancel the subscription
      await api.post('/api/workflows/subscriptions/cancel');

      showSuccess('Subscription Cancelled', 'Your subscription has been cancelled.');

      // Small delay to let the cancellation process
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Rebuild the reauth body — the id_token collected earlier may have
      // been consumed by the first (rejected) attempt, and Apple in
      // particular refuses to re-verify the same one twice. Web Google
      // reauth is not driven from here (that path submits from the GIS
      // callback), so buildDeletionBody's password / native branches cover
      // every case that can reach this point.
      const body = await buildDeletionBody();
      if (!body) {
        setIsDeleting(false);
        return;
      }

      await submitDeletion(body);
    } catch (error: any) {
      console.error('Cancel subscription and delete error:', error);
      showError(
        'Operation Failed',
        error.response?.data?.message || 'Failed to cancel subscription and delete account. Please try again.'
      );
      setIsDeleting(false);
    }
  };

  /**
   * True when the Google reauth flow on this platform needs a rendered GIS
   * button rather than a programmatic native-SDK call. GIS's account picker
   * cannot be launched from a synthetic click, so the main "Delete My
   * Account" button is hidden in this case and the GIS button itself is what
   * both re-authenticates AND triggers deletion.
   */
  const isWebGoogleReauth = activeProvider === 'google' && !isNative;

  /** True when the Apple reauth needs the web SDK popup (not the native sheet). */
  const isWebAppleReauth = activeProvider === 'apple' && !isNative;

  /**
   * Pre-load Apple JS as soon as we know the user is going to use web Apple
   * reauth, so the eventual `signIn()` call inside `collectAppleReauth` runs
   * inside the same user-gesture microtask as the button click. Loading the
   * script inline in the click handler works most of the time but Safari's
   * popup blocker occasionally counts the network wait against the gesture
   * budget and refuses to open the popup — which then looks to the user
   * like the button did nothing.
   */
  useEffect(() => {
    if (step !== 'confirmation') return;
    if (!isWebAppleReauth) return;
    ensureAppleIdentityLoaded().catch((error) => {
      console.warn('[DeleteAccount] Pre-loading Apple sign-in JS failed:', error);
    });
  }, [step, isWebAppleReauth]);

  /**
   * True when the primary confirm button should be clickable. Guards against
   * missing reauth material AND against the identity list still loading.
   *
   * The web-Google path deliberately excludes the primary button — see
   * `isWebGoogleReauth` — so it returns false there regardless of everything
   * else being satisfied.
   */
  const canSubmit = (() => {
    if (isDeleting || oauthInFlight) return false;
    if (confirmText !== confirmKeyword) return false;
    if (identitiesLoading) return false;
    if (!activeProvider) return false;
    if (activeProvider === 'email' && !password.trim()) return false;
    if (isWebGoogleReauth) return false;
    return true;
  })();

  /**
   * Render the Google Identity Services button whenever the confirmation card
   * shows the Google-web reauth path AND the user has already met the
   * "type DELETE / DEACTIVATE" gate. Rendering it earlier would let a user
   * complete Google auth without confirming the keyword — the backend would
   * still reject the delete for the acknowledgement/keyword reasons, but the
   * id_token would have been wasted on an attempt that could not have
   * succeeded and Google would refuse a second in-flight prompt.
   */
  useEffect(() => {
    if (step !== 'confirmation') return;
    if (!isWebGoogleReauth) return;
    if (confirmText !== confirmKeyword) return;
    if (isDeleting) return;

    const container = googleWebButtonContainerRef.current;
    if (!container) return;

    let cancelled = false;
    let controller: { dispose: () => void } | null = null;
    setGoogleWebSetupError(null);

    renderWebGoogleReauthButton(container, {
      onSuccess: async ({ idToken, nonce }) => {
        if (cancelled) return;
        await submitDeletion({ provider: 'google', id_token: idToken, nonce });
      },
      onError: (err) => {
        if (cancelled) return;
        setGoogleWebSetupError(err.message);
      },
    })
      .then((c) => {
        if (cancelled) c.dispose();
        else controller = c;
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error
            ? err.message
            : 'Could not load Google sign-in on this device.';
        setGoogleWebSetupError(message);
      });

    return () => {
      cancelled = true;
      controller?.dispose();
    };
    // Only the gating inputs are in deps on purpose: adding submitDeletion /
    // showError etc. remounts the GIS button on every render, which loses
    // any picker already in flight and re-fetches the GIS script for no
    // benefit.
  }, [step, isWebGoogleReauth, confirmText, confirmKeyword, isDeleting]);

  /**
   * The user has provider identities on file but none of them are usable on
   * this platform (typical case: Google-only account opened in a plain web
   * browser, or Apple-only account opened in native Android). Rendered as a
   * blocking notice so they know why the confirm button is disabled.
   */
  const showPlatformUnavailableNotice =
    !identitiesLoading &&
    connectedAccounts &&
    linkedProviders.length > 0 &&
    availableProviders.length === 0;

  return (
    <AppLayout title="Delete / Deactivate Account" backLink="/settings">
      <div className="container mx-auto px-4 py-6 pb-[44px] md:pb-6 max-w-3xl">
        {/* Header */}
        <div className="mb-6">
          {step !== 'choice' && (
            <Button
              variant="ghost"
              onClick={() => {
                setStep(step === 'confirmation' ? 'warning' : 'choice');
                resetConfirmationFields();
              }}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-2xl flex items-center justify-center">
              <Trash2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Delete / Deactivate Account</h1>
              <p className="text-gray-600 mt-1">
                Delete or temporarily deactivate my account
              </p>
            </div>
          </div>
        </div>

        {/* Step 1: Choose Deletion Type */}
        {step === 'choice' && (
          <div className="space-y-6">
            <p className="text-gray-700 text-center mb-6">
              Please choose how you'd like to proceed with your account:
            </p>

            {/* Soft Delete Option */}
            <Card
              className="border-2 border-amber-200 hover:border-amber-400 cursor-pointer transition-all"
              onClick={() => handleChooseDeletionType('soft')}
            >
              <CardHeader className="pb-3 bg-amber-50">
                <CardTitle className="flex items-center gap-2 text-amber-900">
                  <Power className="h-5 w-5" />
                  Temporarily Deactivate (Recommended)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-gray-800 mb-3">
                  <strong>Your data is preserved and hidden.</strong> You can reactivate your account anytime by logging in again.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <Undo2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Reversible:</strong> Simply log in to reactivate instantly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Database className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Data Preserved:</strong> All your connections, messages, and profile data remain safe</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <UserX className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Hidden Profile:</strong> Your account won't appear in searches while deactivated</span>
                  </li>
                </ul>
                <Button className="w-full mt-4 bg-amber-500 hover:bg-amber-600">
                  Choose Temporary Deactivation
                </Button>
              </CardContent>
            </Card>

            {/* Hard Delete Option */}
            <Card
              className="border-2 border-red-200 hover:border-red-400 cursor-pointer transition-all"
              onClick={() => handleChooseDeletionType('hard')}
            >
              <CardHeader className="pb-3 bg-red-50">
                <CardTitle className="flex items-center gap-2 text-red-900">
                  <AlertTriangle className="h-5 w-5" />
                  Permanently Delete Account
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-gray-800 mb-3">
                  <strong>All your data will be permanently removed.</strong> This action cannot be undone.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <Trash2 className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Irreversible:</strong> Your account cannot be recovered</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Database className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Data Deleted:</strong> Your profile, personal details and billing history are permanently removed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Your Partners Keep Their Records:</strong> Shared referrals and conversations stay with the partner who was on the other side, showing you only as a deleted user</span>
                  </li>
                </ul>
                <Button variant="destructive" className="w-full mt-4">
                  Choose Permanent Deletion
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Warning */}
        {step === 'warning' && (
          <div className="space-y-6">
            {/* Warning Card */}
            <Card className={`border-2 ${deletionType === 'hard' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
              <CardHeader className="pb-3">
                <CardTitle className={`flex items-center gap-2 ${deletionType === 'hard' ? 'text-red-900' : 'text-amber-900'}`}>
                  <AlertTriangle className="h-5 w-5" />
                  {deletionType === 'hard' ? 'This Action Cannot Be Undone' : 'Deactivate Your Account'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={deletionType === 'hard' ? 'text-red-800' : 'text-amber-800'}>
                  {deletionType === 'hard'
                    ? 'Deleting your account is permanent and irreversible. All your data will be permanently removed from our systems and cannot be recovered.'
                    : 'Deactivating your account will hide your profile and data. Your account will be preserved and can be instantly reactivated by logging in again.'}
                </p>
              </CardContent>
            </Card>

            {/* What Happens */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {deletionType === 'hard' ? 'What Will Be Permanently Deleted' : 'What Happens When You Deactivate'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {/* Profile Data */}
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Database className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">Profile & Account Data</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {deletionType === 'hard'
                          ? 'Your profile, business details, and all personal data will be permanently deleted and cannot be recovered.'
                          : 'Your profile information and personal data will be preserved but hidden. Everything will be restored when you log in again.'}
                      </p>
                    </div>
                  </div>

                  {/* Connections */}
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">Network Connections</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {deletionType === 'hard'
                          ? 'You will be removed from your partners\' networks and will no longer appear in their partner lists or in search.'
                          : 'Your connections will be temporarily hidden. Partners won\'t see you in their network until you reactivate.'}
                      </p>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">Messages & Conversations</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {deletionType === 'hard'
                          ? 'Your copy of every conversation is removed. Your partners keep their own copy of what you sent them, shown against a deleted-user label.'
                          : 'Your messages and conversation history will be preserved and restored when you reactivate.'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Acknowledgement */}
            <Card>
              <CardHeader>
                <CardTitle>Acknowledgement Required</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Please confirm that you understand the consequences:
                </p>

                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer p-4 bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors">
                    <Checkbox
                      checked={acknowledgedWarnings}
                      onCheckedChange={(checked) => setAcknowledgedWarnings(checked as boolean)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <span className="text-sm text-gray-900 font-medium block mb-2">
                        I understand and acknowledge:
                      </span>
                      <ul className="text-sm text-gray-700 space-y-1.5 ml-1">
                        {deletionType === 'hard' ? (
                          <>
                            <li className="flex items-start gap-2">
                              <span className="text-red-500 mt-0.5">•</span>
                              <span>This action is <strong>permanent and cannot be undone</strong></span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-red-500 mt-0.5">•</span>
                              <span><strong>My profile and personal data will be permanently deleted</strong> and cannot be recovered</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-red-500 mt-0.5">•</span>
                              <span><strong>I cannot create a new account with the same email</strong> immediately</span>
                            </li>
                          </>
                        ) : (
                          <>
                            <li className="flex items-start gap-2">
                              <span className="text-amber-500 mt-0.5">•</span>
                              <span>My account will be <strong>temporarily deactivated and hidden</strong></span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-amber-500 mt-0.5">•</span>
                              <span><strong>All my data will be preserved</strong> and can be restored</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-green-600 mt-0.5">•</span>
                              <span><strong>I can reactivate instantly</strong> by logging in again</span>
                            </li>
                          </>
                        )}
                      </ul>
                    </div>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons.
                Stacked below `sm`, side by side above it. `min-w-` was forcing both buttons
                wider than a 320px viewport could hold, so the row overflowed its container and
                the labels collided; `basis-0 grow` lets them share the row instead, and
                `h-auto whitespace-normal` lets a label wrap to a second line rather than
                spilling out of the button at large accessibility text sizes. */}
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <Button
                variant="outline"
                onClick={() => setStep('choice')}
                className="w-full sm:w-auto sm:basis-0 sm:grow sm:max-w-[240px] h-auto min-h-10 py-2 whitespace-normal text-center"
              >
                Back to Options
              </Button>
              <Button
                variant="destructive"
                onClick={handleProceedToConfirmation}
                disabled={!allWarningsAcknowledged}
                className="w-full sm:w-auto sm:basis-0 sm:grow sm:max-w-[240px] h-auto min-h-10 py-2 whitespace-normal text-center"
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 'confirmation' && (
          <div className="space-y-6">
            {/* Final Confirmation Card */}
            <Card className={`border-2 ${deletionType === 'hard' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
              <CardHeader>
                <CardTitle className={deletionType === 'hard' ? 'text-red-900' : 'text-amber-900'}>
                  Final Confirmation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className={deletionType === 'hard' ? 'text-red-800' : 'text-amber-800'}>
                  To confirm {deletionType === 'hard' ? 'permanent deletion' : 'deactivation'}, please complete the following steps:
                </p>

                {/* Type Confirmation Keyword */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900">
                    Type <span className="font-mono font-bold bg-gray-200 px-2 py-0.5 rounded">{confirmKeyword}</span> to confirm
                  </label>
                  <Input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={`Type ${confirmKeyword} here`}
                    className="font-mono"
                    autoComplete="off"
                  />
                </div>

                {/* Reauth control(s) — driven by the linked identities the
                    backend just returned. `activeProvider` decides which one
                    is armed; if more than one is available a picker is shown
                    above it. */}
                {identitiesLoading && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Loading your sign-in options…</span>
                  </div>
                )}

                {identitiesError && !identitiesLoading && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    We could not load your sign-in methods. Please refresh the page and try again.
                  </div>
                )}

                {!identitiesLoading && !identitiesError && availableProviders.length > 1 && (
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-gray-900">
                      Confirm your identity with
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {availableProviders.map((provider) => {
                        const label =
                          provider === 'email'
                            ? 'Password'
                            : provider === 'apple'
                              ? 'Sign in with Apple'
                              : 'Sign in with Google';
                        const isActive = activeProvider === provider;
                        return (
                          <button
                            key={provider}
                            type="button"
                            onClick={() => {
                              setChosenProvider(provider);
                              setPassword('');
                            }}
                            className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                              isActive
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!identitiesLoading && !identitiesError && activeProvider === 'email' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-900">
                      Enter your password to verify it's you
                    </label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Your password"
                      autoComplete="current-password"
                    />
                  </div>
                )}

                {!identitiesLoading && !identitiesError && activeProvider === 'apple' && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                    We'll ask Apple to confirm it's you when you press{' '}
                    <strong>{deletionType === 'hard' ? 'Delete My Account Permanently' : 'Deactivate My Account'}</strong>.
                  </div>
                )}

                {!identitiesLoading && !identitiesError && activeProvider === 'google' && isNative && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                    We'll ask Google to confirm it's you when you press{' '}
                    <strong>{deletionType === 'hard' ? 'Delete My Account Permanently' : 'Deactivate My Account'}</strong>.
                  </div>
                )}

                {/* Web-Google reauth: Google Identity Services renders its own
                    button; we hand it a container and it takes over. Native
                    picker cannot be triggered from a synthetic click, so this
                    IS the confirm control on web — the main "Delete My
                    Account" button below is deliberately hidden. */}
                {!identitiesLoading && !identitiesError && isWebGoogleReauth && (
                  <div className="space-y-2">
                    {confirmText !== confirmKeyword ? (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                        Type <span className="font-mono font-bold">{confirmKeyword}</span> above,
                        then use the Google button below to confirm and{' '}
                        {deletionType === 'hard' ? 'permanently delete' : 'deactivate'} your account.
                      </div>
                    ) : (
                      <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <p className="text-sm text-gray-700 mb-3">
                          Click below to confirm with Google. Your account will be{' '}
                          {deletionType === 'hard' ? 'permanently deleted' : 'deactivated'}{' '}
                          immediately after Google verifies you.
                        </p>
                        <div
                          ref={googleWebButtonContainerRef}
                          className="flex justify-center min-h-[44px]"
                        />
                      </div>
                    )}
                    {googleWebSetupError && (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                        {googleWebSetupError} Please refresh the page and try again.
                      </div>
                    )}
                  </div>
                )}

                {showPlatformUnavailableNotice && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <p className="font-semibold mb-1">Confirmation not available on this device</p>
                    <p>
                      Your account signs in with{' '}
                      {linkedProviders
                        .map((p) => (p === 'apple' ? 'Apple' : p === 'google' ? 'Google' : p))
                        .join(' and ')}
                      . To delete or deactivate your account, please open Trade Loop on
                      {' '}
                      {linkedProviders.includes('apple') ? 'an iOS device' : 'the mobile app'}
                      {' '}
                      and try again from Settings → Delete / Deactivate Account.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className={`border-2 ${deletionType === 'hard' ? 'border-blue-200 bg-blue-50' : 'border-green-200 bg-green-50'}`}>
              <CardHeader>
                <CardTitle className={deletionType === 'hard' ? 'text-blue-900' : 'text-green-900'}>
                  {deletionType === 'hard' ? 'We\'re Sorry to See You Go' : 'Easy to Come Back'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deletionType === 'hard' ? (
                  <>
                    <p className="text-blue-800 mb-3">
                      Before you permanently delete your account, please consider:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-blue-700">
                      <li>Your network partners value your connection</li>
                      <li>You lose access to your referral history for good</li>
                      <li>You'll need to create a new account to return</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p className="text-green-800 mb-3">
                      Good news - reactivating is instant and easy:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-green-700">
                      <li>All your data will be preserved and waiting for you</li>
                      <li>Simply log in again to reactivate instantly</li>
                      <li>Your connections, messages, and profile will be restored</li>
                    </ul>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons.
                These two are the ones App Review exercises under Guideline 5.1.1(v), so they
                have to hold their labels at 320px and at the largest accessibility text size.
                "Delete My Account Permanently" does not fit a fixed-width button on a small
                phone: the old `min-w-[120px]` / `min-w-[160px]` pair overflowed the flex row and
                ran the two labels into each other. Stacked below `sm`, shared-width above it,
                and `h-auto whitespace-normal` so a long label wraps inside the button instead of
                escaping it. `shrink-0` keeps the spinner from being squashed by the wrapping
                text (the icon already gets it from the Button base classes). */}
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <Button
                variant="outline"
                onClick={() => navigate('/settings')}
                disabled={isDeleting || Boolean(oauthInFlight)}
                className="w-full sm:w-auto sm:basis-0 sm:grow sm:max-w-[240px] h-auto min-h-10 py-2 whitespace-normal text-center"
              >
                Cancel &amp; Keep Account
              </Button>
              {/* On web-Google reauth the GIS button IS the confirm control
                  (see `isWebGoogleReauth` above), so the destructive button
                  is hidden — a disabled duplicate would be more confusing
                  than helpful. Still shown while `isDeleting` so the spinner
                  is visible after the GIS callback fires. */}
              {(!isWebGoogleReauth || isDeleting) && (
                <Button
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  disabled={!canSubmit && !isDeleting}
                  className="w-full sm:w-auto sm:basis-0 sm:grow sm:max-w-[280px] h-auto min-h-10 py-2 whitespace-normal text-center"
                >
                  {isDeleting || oauthInFlight ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 shrink-0"></div>
                      {oauthInFlight
                        ? 'Confirming…'
                        : deletionType === 'hard' ? 'Deleting...' : 'Deactivating...'}
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      {deletionType === 'hard' ? 'Delete My Account Permanently' : 'Deactivate My Account'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Active Subscription Modal */}
        <Dialog open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-amber-600" />
                </div>
                <DialogTitle className="text-xl">Active Subscription</DialogTitle>
              </div>
              <DialogDescription className="text-base pt-2">
                You currently have an active {subscriptionDetails?.plan || ''} subscription.
                Would you like to cancel your subscription and {deletionType === 'hard' ? 'permanently delete' : 'deactivate'} your account?
              </DialogDescription>
            </DialogHeader>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-900 font-medium mb-1">
                    This will cancel your subscription immediately
                  </p>
                  <p className="text-sm text-amber-800">
                    {deletionType === 'hard'
                      ? 'Your subscription will be cancelled and your account will be permanently deleted. This action cannot be undone.'
                      : 'Your subscription will be cancelled and your account will be deactivated. You can reactivate by logging in again, but you\'ll need to resubscribe separately.'}
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSubscriptionModal(false);
                  setIsDeleting(false);
                }}
                disabled={isDeleting}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelSubscriptionAndDelete}
                disabled={isDeleting}
                className="w-full sm:w-auto"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  'Yes, Cancel & ' + (deletionType === 'hard' ? 'Delete' : 'Deactivate')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default DeleteAccountPage;
